import { isValidUSState } from '@/constants/locationConstants'
import { UserTaxEstimate } from './userTaxEstimates.slice'

export interface EstimateCoverage {
  /** Federal records are absent for at least one quarter of the year. */
  missingFederal: boolean
  /**
   * Home-state records are absent for at least one quarter. False when the home
   * state is unusable, because none can be created; read it together with
   * `homeStateUnusable`.
   */
  missingHomeState: boolean
  /** The profile's home state is absent, or not a state the engine can price. */
  homeStateUnusable: boolean
}

/**
 * Mirrors `getQuarterlyTaxEstimateCoverage` on the API, which the admin record
 * and the quarterly checklist both need without a round-trip. `isValidUSState`
 * is the sanctioned mirror of the backend's `USStates` validation.
 */
export const findEstimateCoverage = ({
  estimates,
  taxYear,
  expectedPerType,
  homeState,
}: {
  estimates: UserTaxEstimate[]
  taxYear: string
  expectedPerType: number
  homeState: string | null | undefined
}): EstimateCoverage => {
  const homeStateUnusable = !homeState || !isValidUSState(homeState)
  if (expectedPerType === 0) {
    return { missingFederal: false, missingHomeState: false, homeStateUnusable }
  }

  const forYear = estimates.filter((estimate) =>
    estimate.taxQuarter.startsWith(taxYear)
  )
  // `user_tax_estimates` carries no unique constraint, so a duplicate row for
  // one quarter can outnumber a genuinely missing quarter. Count distinct
  // quarters covered, not rows.
  const distinctQuartersCovered = (rows: UserTaxEstimate[]) =>
    new Set(rows.map((row) => row.taxQuarter)).size

  return {
    missingFederal:
      distinctQuartersCovered(forYear.filter((e) => e.type === 'federal')) <
      expectedPerType,
    missingHomeState:
      !homeStateUnusable &&
      distinctQuartersCovered(
        forYear.filter((e) => e.type === 'state' && e.filingState === homeState)
      ) < expectedPerType,
    homeStateUnusable,
  }
}

/** Whether anything is left for the repair to create. */
export const hasGenerableGap = (coverage: EstimateCoverage): boolean =>
  coverage.missingFederal || coverage.missingHomeState
